import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Users, AlertTriangle, CheckCircle, 
  RefreshCw, Mail, ChevronRight, Shield, Clock, Send,
  TrendingUp, UserCheck, UserX, Baby, PartyPopper
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgreementAnalyticsPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [resending, setResending] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Program filtering
  const [programsList, setProgramsList] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('all');

  useEffect(() => {
    fetchPrograms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (programsList.length > 0 || selectedProgramId === 'all') {
      fetchAnalytics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramId]);

  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/platform/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProgramsList(data);
        fetchAnalytics();
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/agreements/analytics`;
      if (selectedProgramId && selectedProgramId !== 'all') {
        url += `?program_id=${selectedProgramId}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const resendConsentEmail = async (youthId, youthName) => {
    setResending(youthId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/agreements/resend-parent-consent/${youthId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Consent email resent for ${youthName}`);
        fetchAnalytics();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to resend email');
      }
    } catch (error) {
      toast.error('Failed to resend email');
    } finally {
      setResending(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: colors.primary }} />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const agreements = analytics?.agreement_breakdown || [];
  const needsAttention = analytics?.users_needing_attention || [];
  const pendingParent = analytics?.pending_parent_requests || [];

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="text-white" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Shield className="w-6 h-6" />
                Agreement Analytics

          {/* Program Filter */}
          <div className="flex items-center gap-3 mt-4">
            <label className="text-sm font-medium text-white/90">Program:</label>
            <select
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="px-4 py-2 rounded-lg border text-sm font-medium"
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'white'
              }}
            >
              <option value="all" style={{ backgroundColor: colors.surface, color: colors.text }}>
                📊 All Programs (Platform-Wide)
              </option>
              {programsList.map(prog => (
                <option 
                  key={prog.id} 
                  value={prog.id}
                  style={{ backgroundColor: colors.surface, color: colors.text }}
                >
                  {prog.name} ({prog.city})
                </option>
              ))}
            </select>
          </div>

              </h1>
              <p className="text-white/80 text-sm">Monitor agreement compliance and parent consent status</p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <SummaryCard 
              icon={Users} 
              label="Total Youth" 
              value={summary.total_youth || 0} 
              color="bg-white/20"
            />
            <SummaryCard 
              icon={UserCheck} 
              label="Fully Compliant" 
              value={summary.fully_compliant || 0}
              subtext={`${summary.compliance_rate || 0}%`}
              color="bg-green-500/30"
            />
            <SummaryCard 
              icon={AlertTriangle} 
              label="Need Attention" 
              value={summary.needing_attention || 0}
              color="bg-amber-500/30"
            />
            <SummaryCard 
              icon={Baby} 
              label="Minors" 
              value={summary.minors_count || 0}
              subtext={`${summary.parent_consent_pending || 0} pending`}
              color="bg-purple-500/30"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="attention" className="gap-2">
              <AlertTriangle className="w-4 h-4" />
              Needs Attention ({needsAttention.length})
            </TabsTrigger>
            <TabsTrigger value="consent" className="gap-2">
              <Mail className="w-4 h-4" />
              Parent Consent ({pendingParent.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Compliance Gauge */}
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Overall Compliance Rate
                </h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Progress value={summary.compliance_rate || 0} className="h-4" />
                  </div>
                  <span className="text-2xl font-bold text-primary">
                    {summary.compliance_rate || 0}%
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {summary.fully_compliant || 0} of {summary.total_youth || 0} youth have signed all required agreements
                  {summary.minors_count > 0 && ' and obtained parent consent'}
                </p>
              </Card>

              {/* Agreement Breakdown */}
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Agreement Breakdown
                </h3>
                <div className="space-y-4">
                  {agreements.map((agreement) => (
                    <div key={agreement.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{agreement.short_title}</span>
                        <span className="text-sm text-gray-500">
                          {agreement.signed_count}/{summary.total_youth || 0} signed
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={agreement.percentage} className="flex-1 h-2" />
                        <span className={`text-sm font-semibold ${
                          agreement.percentage >= 90 ? 'text-green-600' :
                          agreement.percentage >= 70 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {agreement.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Parent Consent Summary */}
              {summary.minors_count > 0 && (
                <Card className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-500" />
                    Parent Consent Status
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-gray-900">{summary.minors_count}</p>
                      <p className="text-sm text-gray-500">Total Minors</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{summary.parent_consent_completed}</p>
                      <p className="text-sm text-green-700">Consent Received</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{summary.parent_consent_pending}</p>
                      <p className="text-sm text-amber-700">Awaiting Consent</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Needs Attention Tab */}
          <TabsContent value="attention">
            <Card className="divide-y">
              {needsAttention.length === 0 ? (
                <div className="p-8 text-center">
                  <PartyPopper className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg">All Clear!</h3>
                  <p className="text-gray-500">All youth are fully compliant with agreements.</p>
                </div>
              ) : (
                needsAttention.map((youth) => (
                  <div key={youth.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        youth.is_minor ? 'bg-purple-100' : 'bg-gray-100'
                      }`}>
                        {youth.is_minor ? (
                          <Baby className="w-5 h-5 text-purple-600" />
                        ) : (
                          <UserX className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">{youth.name}</p>
                        <p className="text-sm text-gray-500">{youth.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {youth.signed_count}/{youth.total_required} signed
                          </Badge>
                          {youth.is_minor && (
                            <Badge 
                              variant={youth.parent_consent_status === 'pending' ? 'secondary' : 'outline'}
                              className="text-xs"
                            >
                              {youth.parent_consent_status === 'pending' ? 'Parent: Pending' : 'Parent: Not Requested'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-amber-600 font-medium">{youth.issue}</p>
                      {youth.is_minor && youth.parent_email && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 gap-1"
                          onClick={() => resendConsentEmail(youth.id, youth.name)}
                          disabled={resending === youth.id}
                        >
                          {resending === youth.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <Send className="w-3 h-3" />
                          )}
                          Resend Email
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </Card>
          </TabsContent>

          {/* Parent Consent Tab */}
          <TabsContent value="consent">
            <Card className="divide-y">
              {pendingParent.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg">No Pending Requests</h3>
                  <p className="text-gray-500">All parent consent requests have been completed.</p>
                </div>
              ) : (
                pendingParent.map((request, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{request.youth_name}</p>
                        <p className="text-sm text-gray-500">Parent: {request.parent_email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Requested: {new Date(request.requested_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Awaiting Signature
                    </Badge>
                  </div>
                ))
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Refresh Button */}
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={fetchAnalytics} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh Data
          </Button>
        </div>
      </div>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className={`${color} rounded-xl p-4 text-white`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-5 h-5" />
      <span className="text-sm opacity-90">{label}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {subtext && <p className="text-xs opacity-80">{subtext}</p>}
  </div>
);

export default AgreementAnalyticsPage;
